import { ProjectRunConfigurationType, RegionSetStatusType } from "@app/projects/enumerators";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";

// needs to match the enum of the same name in the backend
export enum EcdTrendingArrowEnum {
    Unknown,
    RedUp = "redUp",
    RedDown = "redDown",
    RedNeutral = "redNeutral",
    BlueUp = "blueUp",
    BlueDown = "blueDown",
    BlueNeutral = "blueNeutral",
    NeutralUp = "neutralUp",
    NeutralDown = "neutralDown",
    NeutralNeutral = "neutralNeutral",
}

export interface EcdWidgetData {
    adequacyBreakdownDataModel: AdequacyBreakdownData;
    countyStatusOverviewModels: CountyStatusData[];
    gapCountByAgeModels: GapsByAgeData[];
    gapOverviewDataModels: GapOverviewData[];
    networkTaxonomyLineOfBusinessOverviewModels: LineOfBusinessData[];
}

export interface AdequacyBreakdownData {
    closedGaps: string;
    countySpecialtyGroupCount: string;
    countySpecialtyGroupCountPrior: string;
    countySpecialtyGroupCountTrend: EcdTrendingArrowEnum;
    gapPercent: string;
    gapPercentPrior: string;
    gapPercentTrend: EcdTrendingArrowEnum;
    medianGapAge: string;
    medianGapAgePrior: string;
    medianGapAgeTrend: EcdTrendingArrowEnum;
    newGaps: string;
    overallGapCount: string;
    overallGapCountPrior: string;
    overallGapCountTrend: EcdTrendingArrowEnum;
}

export interface GapOverviewData {
    gapCount: number;
    week: string;
    weekEnd: string;
}

export interface CompositeFillableGapsData {
    fillableByOneGapCount: number;
    fillableByOnePercentage: number;
    fillableGapCount: number;
    fillablePercentage: number;
    gapCount: number;
}

export interface GapsByAgeData {
    gapAge30: number;
    percentOfTotalGaps30: number
    gapAge60: number;
    percentOfTotalGaps60: number
    gapAge90: number;
    percentOfTotalGaps90: number
    gapAgeOver90: number;
    percentOfTotalGapsOver90: number
    month: string;
}

export interface TopSpecialtiesByGapData {
    fillableGapCount: number;
    specialtyGroupCode: string;
    specialtyGroupDisplayName: string;
    specialtyGroupId: number;
    unfillableGapCount: number;
}

export interface ExceptionsData {
    // as far as I can tell this has to be a flat data structure for recharts
    exceptionsAppealed: number;
    exceptionsApproved: number;
    exceptionsDenied: number;
    exceptionsPending: number;
    exceptionsSubmitted: number;
    month: string;
}

export interface TopCountiesByGapData {
    canonicalRegionSetId: number;
    countyName: string;
    countySSA: string;
    gaps: string;
    projects: string;
}

export interface LineOfBusinessData {
    approvedExceptionCount: string;
    compositeFillableGapCount: string;
    countyCount: string;
    gapCount: string;
    gapPercent: string;
    medianGapAge: string;
    networkTaxonomyLOB: string;
}

export interface CountyStatusData {
    compositeFillableGapCount: string;
    countyCount: string;
    countyStatus: string;
    gapCount: string;
    gapPercent: string;
    medianGapAge: string;
}

export enum TimeFrameOptions {
    Three = "Last 3 Months",
    Six = "Last 6 Months",
    Twelve = "Last 12 Months",
    Fifteen = "Last 15 Months",
}

export interface EcdApiFilters {
    canonicalRegionSetIds: number[];
    stateAbbreviations: string[];
    networkTaxonomyLOBs: string[];
    customProjectIds: number[];
    countyStatuses: RegionSetStatusType[];
    specialtyGroupIds: number[];
    projectRunConfigurationTypes: ProjectRunConfigurationType[];
}

export interface EcdFilterStorageObject {
    default: EcdFilterStorageItem;
    [key: string]: EcdFilterStorageItem;
}

export interface EcdFilterStorageItem {
    counties: string[];
    countyStatuses: DropdownItem<string>[];
    isDefaultFilters: boolean;
    networkTaxonomyLOBs: DropdownItem<string>[];
    projectIds: DropdownItem<string>[];
    specialtyGroupIds: DropdownItem<string>[];
    tags: string[];
}

export enum EcdTilesEnum {
    AdequacyBreakdown,
    CompositeFillableGaps,
    CountyStatusOverview,
    Exceptions,
    GapOverview,
    GapsByAge,
    TopSpecialtiesByGap,
    LineOfBusinessOverview,
    TopCountiesByGap,
}

/* Recharts defines the axis function props as "any," so we'll provide our own */
export interface EcdAxisTickProps {
    payload?: {
        value: string;
    }
    x?: number;
    xOffset?: number;
    y?: number;
}

export interface ColorSet {
    arrowNegative: string;
    arrowPositive: string;
    barHoverBackground: string;
    exceptionsPrimary: string;
    fillableGapsPrimary: string;
    fillableGapsSecondary: string;
    gapsPrimary: string;
    unfillableGaps: string;
}

export interface GapCountDrillInModel {
    customProjectId: number;
    fillableGapCount: number;
    gapCount: number;
    projectName: string;
    unfillableGapCount: number;
}

export interface CompositeFillableGapCountDrillInModel extends GapCountDrillInModel {
    fillableByOneGapCount: number;
}

export interface AdequacyBreakdownDrillInModel {
    customProjectId: number;
    projectName: string;
    overallGapCount: number;
    newGaps: number;
    closedGaps: number;
}

export interface ExceptionsDrillInModel {
    customProjectId: number;
    exceptionsAppealed: number;
    exceptionsApproved: number;
    exceptionsDenied: number;
    exceptionsPending: number;
    exceptionsSubmitted: number;
    projectName: string;
}

export enum EcdReportGenerationWizardContextSteps {
  "ExportDetails",
  "ExportPreview"
}

export interface VisualizationOption {
  key: string;
  title: string;
  value: boolean;
  onChange: () => void;
}

export interface EcdReportGenerationStepProps {
  pageIsValid?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface DisplayFilters {
  counties: string[];
  countyStatuses: string[];
  excludedProjects: string[];
  includedProjects: string[];
  includedProjectsTotal: number;
  lineOfBusiness: string[];
  specialties: string[];
  specialtyTotal: number;
  states: string[];
  tags: string[];
  tagsTotal: number;
}
